import React, { useEffect, useState } from 'react';
import api from '../services/api';

/**
 * Brand override management — per appointment type or per department.
 *
 * Resolves issue #139 — practice 4 (Better Health Primary Care) sells the
 * Better Sleep brand under specific appointment types. This UI lets staff
 * tag each appointment_type with its own name / logo / email sender so
 * patient-facing emails, SMS, and consent forms render under the right
 * brand without spinning up a separate practice row.
 *
 * commons #251 — a row can instead be scoped to a department (Sleep Plano,
 * Sleep Allen), since most Better Sleep visits use generic appointment
 * types shared with primary care. An appointment-type row wins over a
 * department row; both fall through to the practice.
 *
 * Backend contract:
 *   GET    /practices/:practiceId/appointment-type-brand-overrides
 *   POST   /practices/:practiceId/appointment-type-brand-overrides
 *   DELETE /practices/:practiceId/appointment-type-brand-overrides/:id
 *   POST   /practices/:practiceId/appointment-type-brand-overrides/:id/logo
 *   DELETE /practices/:practiceId/appointment-type-brand-overrides/:id/logo
 *   GET    /practices/:practiceId/appointment-types/known
 */
function AppointmentTypeBrandOverridesTab({ practiceId }) {
  const [overrides, setOverrides] = useState([]);
  const [knownTypes, setKnownTypes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editing, setEditing] = useState(null);

  const reload = async () => {
    setLoading(true);
    setError('');
    try {
      const [overridesRes, knownRes, deptRes] = await Promise.all([
        api.request(`/practices/${practiceId}/appointment-type-brand-overrides`),
        api.request(`/practices/${practiceId}/appointment-types/known`),
        api.request(`/practices/${practiceId}/voice/departments`),
      ]);
      if (overridesRes?.success) setOverrides(overridesRes.data || []);
      if (knownRes?.success) setKnownTypes(knownRes.data || []);
      if (deptRes?.success) setDepartments((deptRes.data || []).filter(d => d.enabled !== 0));
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (practiceId) reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceId]);

  const handleSave = async (form) => {
    setError(''); setSuccess('');
    try {
      const res = await api.request(`/practices/${practiceId}/appointment-type-brand-overrides`, {
        method: 'POST',
        body: JSON.stringify(form),
      });
      if (!res?.success) throw new Error(res?.error || 'Save failed');
      setSuccess('Brand override saved');
      setEditing(null);
      await reload();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete brand override for "${scopeLabel(row)}"?`)) return;
    setError(''); setSuccess('');
    try {
      const res = await api.request(`/practices/${practiceId}/appointment-type-brand-overrides/${row.id}`, {
        method: 'DELETE',
      });
      if (!res?.success) throw new Error(res?.error || 'Delete failed');
      setSuccess('Brand override deleted');
      await reload();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleLogoUpload = async (row, file) => {
    setError(''); setSuccess('');
    try {
      const fd = new FormData();
      fd.append('logo', file);
      const token = localStorage.getItem('nh_auth_token') || localStorage.getItem('nh_context_token');
      const res = await fetch(`/api/practices/${practiceId}/appointment-type-brand-overrides/${row.id}/logo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!data?.success) throw new Error(data?.error || 'Logo upload failed');
      setSuccess('Logo uploaded');
      await reload();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleLogoDelete = async (row) => {
    if (!window.confirm('Remove the brand logo?')) return;
    setError(''); setSuccess('');
    try {
      const res = await api.request(`/practices/${practiceId}/appointment-type-brand-overrides/${row.id}/logo`, {
        method: 'DELETE',
      });
      if (!res?.success) throw new Error(res?.error || 'Delete failed');
      setSuccess('Logo removed');
      await reload();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Brand Overrides</h3>
        <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
          Override the practice's name, logo, and email-sender name for specific appointment types or
          departments. Patients with a matching appointment receive consent forms, emails, SMS and the
          feedback page branded with these values instead of the practice defaults. An appointment-type
          override wins over a department override; empty fields fall through to the practice settings.
        </p>
      </div>

      {error && <div style={{ background: '#fef2f2', color: '#991b1b', padding: 10, borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{error}</div>}
      {success && <div style={{ background: '#ecfdf5', color: '#065f46', padding: 10, borderRadius: 6, marginBottom: 12, fontSize: 13 }}>{success}</div>}

      <button type="button" style={btnPrimary} onClick={() => setEditing({ appointment_type: '', department_id: null, brand_name: '', brand_email_sender_name: '', brand_email_sender_address: '', brand_sms_sender_phone: '', is_active: 1 })}>
        + Add brand override
      </button>

      {loading ? (
        <div style={{ padding: 16, color: '#6b7280' }}>Loading...</div>
      ) : overrides.length === 0 ? (
        <div style={{ padding: 16, color: '#6b7280', fontSize: 13, marginTop: 12 }}>
          No brand overrides yet. All appointments use the practice default.
        </div>
      ) : (
        <table style={{ width: '100%', marginTop: 16, borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              <th style={th}>Applies to</th>
              <th style={th}>Brand Name</th>
              <th style={th}>Logo</th>
              <th style={th}>Email Sender Name</th>
              <th style={th}>SMS Sender</th>
              <th style={th}>Active</th>
              <th style={th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {overrides.map(row => (
              <tr key={row.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={td}>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>{row.appointment_type ? 'Appointment type' : 'Department'}</div>
                  <strong>{scopeLabel(row)}</strong>
                </td>
                <td style={td}>{row.brand_name || <em style={{ color: '#9ca3af' }}>(use practice)</em>}</td>
                <td style={td}>
                  {row.logo_signed_url ? (
                    <img src={row.logo_signed_url} alt="logo" style={{ maxHeight: 36, maxWidth: 100, borderRadius: 4, border: '1px solid #e5e7eb' }} />
                  ) : <em style={{ color: '#9ca3af' }}>(none)</em>}
                  <div style={{ marginTop: 6 }}>
                    <input type="file" accept="image/*" id={`logo-${row.id}`} style={{ display: 'none' }}
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) handleLogoUpload(row, f); e.target.value = ''; }} />
                    <label htmlFor={`logo-${row.id}`} style={{ ...btnSecondarySm, cursor: 'pointer' }}>
                      {row.brand_logo_url ? 'Replace' : 'Upload'}
                    </label>
                    {row.brand_logo_url && (
                      <button type="button" style={{ ...btnSecondarySm, marginLeft: 6 }} onClick={() => handleLogoDelete(row)}>Remove</button>
                    )}
                  </div>
                </td>
                <td style={td}>{row.brand_email_sender_name || <em style={{ color: '#9ca3af' }}>(use practice)</em>}</td>
                <td style={td}>{row.brand_sms_sender_phone || <em style={{ color: '#9ca3af' }}>(use practice)</em>}</td>
                <td style={td}>{row.is_active ? 'Yes' : 'No'}</td>
                <td style={td}>
                  <button type="button" style={btnSecondarySm} onClick={() => setEditing(row)}>Edit</button>
                  <button type="button" style={{ ...btnSecondarySm, marginLeft: 6, color: '#b91c1c' }} onClick={() => handleDelete(row)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editing && (
        <BrandOverrideModal
          initial={editing}
          knownTypes={knownTypes}
          departments={departments}
          existingTypes={overrides.map(o => o.appointment_type).filter(Boolean)}
          existingDepartmentIds={overrides.filter(o => !o.appointment_type && o.department_id).map(o => o.department_id)}
          onCancel={() => setEditing(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

function scopeLabel(row) {
  if (row.appointment_type) return row.appointment_type;
  return row.department_name || `Department #${row.department_id}`;
}

function BrandOverrideModal({ initial, knownTypes, departments, existingTypes, existingDepartmentIds, onCancel, onSave }) {
  const isNew = !initial.id;
  const [scope, setScope] = useState(initial.department_id && !initial.appointment_type ? 'department' : 'appointment_type');
  const [form, setForm] = useState({
    appointment_type: initial.appointment_type || '',
    department_id: initial.department_id || '',
    brand_name: initial.brand_name || '',
    brand_email_sender_name: initial.brand_email_sender_name || '',
    brand_email_sender_address: initial.brand_email_sender_address || '',
    brand_sms_sender_phone: initial.brand_sms_sender_phone || '',
    is_active: initial.is_active ? 1 : 0,
  });
  const [submitting, setSubmitting] = useState(false);

  // For new entries, restrict appointment_type to known types the practice
  // hasn't already overridden. For edits, the appointment_type is locked.
  const availableTypes = knownTypes.filter(t => !existingTypes.includes(t.appointment_type));
  const availableDepartments = departments.filter(d => !existingDepartmentIds.includes(d.id));
  const scopeChosen = scope === 'department' ? !!form.department_id : !!form.appointment_type.trim();

  const submit = async (e) => {
    e.preventDefault();
    if (!scopeChosen) return;
    setSubmitting(true);
    // Exactly one scope goes to the server; the other is cleared.
    await onSave(scope === 'department'
      ? { ...form, appointment_type: null, department_id: parseInt(form.department_id, 10) }
      : { ...form, department_id: null });
    setSubmitting(false);
  };

  return (
    <div style={modalBackdrop} onClick={onCancel}>
      <div style={modalCard} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>{isNew ? 'Add brand override' : 'Edit brand override'}</h3>
        <form onSubmit={submit}>
          {isNew && (
            <div style={field}>
              <label style={label}>Applies to</label>
              <div style={{ display: 'flex', gap: 16, fontSize: 13 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="radio" name="scope" checked={scope === 'appointment_type'} onChange={() => setScope('appointment_type')} />
                  An appointment type
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="radio" name="scope" checked={scope === 'department'} onChange={() => setScope('department')} />
                  A department
                </label>
              </div>
            </div>
          )}
          {scope === 'department' ? (
            <div style={field}>
              <label style={label}>Department</label>
              {isNew ? (
                <select required value={form.department_id} onChange={(e) => setForm({ ...form, department_id: e.target.value })} style={input}>
                  <option value="">— Select a department —</option>
                  {availableDepartments.map(d => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              ) : (
                <input type="text" value={initial.department_name || `Department #${initial.department_id}`} disabled style={{ ...input, background: '#f9fafb' }} />
              )}
            </div>
          ) : (
            <div style={field}>
              <label style={label}>Appointment type</label>
              {isNew ? (
                <select required value={form.appointment_type} onChange={(e) => setForm({ ...form, appointment_type: e.target.value })} style={input}>
                  <option value="">— Select an appointment type —</option>
                  {availableTypes.map(t => (
                    <option key={t.appointment_type} value={t.appointment_type}>{t.appointment_type} ({t.n} appts)</option>
                  ))}
                </select>
              ) : (
                <input type="text" value={form.appointment_type} disabled style={{ ...input, background: '#f9fafb' }} />
              )}
            </div>
          )}
          <div style={field}>
            <label style={label}>Brand name (shown in consent form titles, emails, SMS)</label>
            <input type="text" value={form.brand_name} onChange={(e) => setForm({ ...form, brand_name: e.target.value })} placeholder="e.g. Better Sleep" style={input} />
          </div>
          <div style={field}>
            <label style={label}>Email sender name (the "From" name patients see)</label>
            <input type="text" value={form.brand_email_sender_name} onChange={(e) => setForm({ ...form, brand_email_sender_name: e.target.value })} placeholder="e.g. Better Sleep" style={input} />
          </div>
          <div style={field}>
            <label style={label}>Email sender address (leave blank to keep practice default)</label>
            <input type="email" value={form.brand_email_sender_address} onChange={(e) => setForm({ ...form, brand_email_sender_address: e.target.value })} placeholder="e.g. noreply@newtonhealthcore.com" style={input} />
          </div>
          <div style={field}>
            <label style={label}>SMS sender phone (leave blank to keep practice default)</label>
            <input type="text" value={form.brand_sms_sender_phone} onChange={(e) => setForm({ ...form, brand_sms_sender_phone: e.target.value })} placeholder="e.g. +15075790298" style={input} />
          </div>
          <div style={field}>
            <label style={{ ...label, display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={!!form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked ? 1 : 0 })} />
              Active
            </label>
          </div>
          <p style={{ fontSize: 11, color: '#6b7280', marginBottom: 12 }}>
            Logo upload is available after saving. Empty fields fall back to the practice defaults.
          </p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" style={btnSecondary} onClick={onCancel} disabled={submitting}>Cancel</button>
            <button type="submit" style={btnPrimary} disabled={submitting || !scopeChosen}>
              {submitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const th = { textAlign: 'left', padding: '8px 10px', fontSize: 12, fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' };
const td = { padding: '10px', verticalAlign: 'top' };
const btnPrimary = { padding: '8px 14px', fontSize: 13, fontWeight: 500, color: '#fff', background: '#4f46e5', border: 'none', borderRadius: 6, cursor: 'pointer' };
const btnSecondary = { padding: '8px 14px', fontSize: 13, fontWeight: 500, color: '#374151', background: '#fff', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer' };
const btnSecondarySm = { padding: '4px 10px', fontSize: 12, fontWeight: 500, color: '#374151', background: '#fff', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer' };
const modalBackdrop = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalCard = { background: '#fff', borderRadius: 8, padding: 24, width: '92%', maxWidth: 540, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' };
const field = { marginBottom: 12 };
const label = { display: 'block', fontSize: 12, fontWeight: 500, color: '#374151', marginBottom: 4 };
const input = { width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box' };

export default AppointmentTypeBrandOverridesTab;
